import styled, { css, DefaultTheme } from 'styled-components';

import { ButtonProps } from '.';

export type WrapperProps = {
  hasIcon: boolean;
  darkBg: boolean;
  dottedBorder?: boolean;
  underlineText?: boolean;
} & Pick<ButtonProps, 'size' | 'fullWidth' | 'variant'>;

const wrapperModifiers = {
  XXSmall: (theme: DefaultTheme) => css`
    font-size: ${theme.FONT_SIZE.XXS};
  `,

  XSmall: (theme: DefaultTheme) => css`
    font-size: ${theme.FONT_SIZE.XS};
  `,

  small: (theme: DefaultTheme) => css`
    font-size: ${theme.FONT_SIZE.SM};
  `,

  medium: (theme: DefaultTheme) => css`
    font-size: ${theme.FONT_SIZE.MD};
    padding: ${theme.SPACING.X_SMALL} ${theme.SPACING.MEDIUM};
  `,

  large: (theme: DefaultTheme) => css`
    font-size: ${theme.FONT_SIZE.LG};
    padding: ${theme.SPACING.X_SMALL} ${theme.SPACING.LARGE};
  `,

  fullWidth: () => css`
    width: 100%;
  `,

  withIcon: (theme: DefaultTheme) => css`
    svg {
      width: 1.5rem;

      & + span {
        margin-left: ${theme.SPACING.XX_SMALL};
      }
    }
  `,

  outline: (theme: DefaultTheme) => css`
    border: 1px solid ${theme.COLORS.PRIMARY};
    background: transparent;
    color: ${theme.COLORS.PRIMARY};
  `,

  minimal: (theme: DefaultTheme) => css`
    border: none;
    background: transparent;
    color: ${theme.COLORS.PRIMARY};

    span {
      position: relative;
      text-decoration: none;
      color: ${theme.COLORS.PRIMARY};

      &::before,
      &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 2px;
        bottom: 0;
        background-color: ${theme.COLORS.PRIMARY};
        transition: width 0.3s ease;
      }

      &::before {
        left: 50%;
        transform: translateX(-50%);
      }

      &::after {
        right: 50%;
        transform: translateX(50%);
      }

      &:hover::before,
      &:hover::after {
        width: 100%;
      }
    }

    &:disabled {
      cursor: not-allowed;
      color: ${theme.COLORS.GRAY_50};
      span {
        color: ${theme.COLORS.GRAY_50};

        &::before,
        &::after {
          background-color: ${theme.COLORS.GRAY_50};
        }
      }
    }
  `,

  primary: (theme: DefaultTheme) => css`
    background: ${theme.COLORS.PRIMARY};
    color: ${theme.COLORS.BLACK};
  `,

  darkBg: (theme: DefaultTheme) => css`
    background: ${theme.COLORS.GRAY_60};
    color: ${theme.COLORS.GRAY_40};
  `,

  red: (theme: DefaultTheme) => css`
    color: ${theme.COLORS.WHITE_1};
    background: ${theme.COLORS.RED_2};

    &:hover {
      background: ${theme.COLORS.RED_3};
    }

    &:active {
      background: ${theme.COLORS.RED_4};
    }
  `,

  disabled: (theme: DefaultTheme, variant: string | undefined) => css`
    &:disabled {
      cursor: not-allowed;
      filter: saturate(100%);
      transform: scale(1);
      color: ${variant === 'minimal' ? theme.COLORS.GRAY_50 : theme.COLORS.GRAY_40};
      background: ${variant === 'primary' ? theme.COLORS.GRAY_80 : 'transparent'};
      border: 1px solid ${theme.COLORS.TEXT_SECONDARY};

      &:hover {
        transform: scale(1);
        span {
          &::before,
          &::after {
            width: 0;
          }
        }
      }
    }
  `,
};

export const Wrapper = styled.button<WrapperProps>`
  ${({
    theme,
    size,
    fullWidth,
    hasIcon,
    variant,
    disabled,
    darkBg,
    dottedBorder,
    underlineText,
  }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${theme.COLORS.PRIMARY};
    color: ${theme.COLORS.WHITE_1};
    font-family: ${theme.FONT_FAMILY.PRIMARY};
    border: 0;
    border-radius: 0.5rem;
    cursor: pointer;
    padding: 2px 8px;
    text-decoration: none;
    transition: all 0.2s ease-in-out;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      opacity: 0.8;
    }

    .icon {
      margin-right: 8px;
    }

    ${!!size && wrapperModifiers[size](theme)};
    ${!!fullWidth && wrapperModifiers.fullWidth()};
    ${!!hasIcon && wrapperModifiers.withIcon(theme)};
    ${!!variant && wrapperModifiers[variant](theme)};
    ${disabled && wrapperModifiers.disabled(theme, variant)};
    ${darkBg && wrapperModifiers.darkBg(theme)};
    ${dottedBorder &&
    css`
      border: 2px dashed ${theme.COLORS.PRIMARY} !important;
      background: transparent !important;
    `}
    ${underlineText &&
    css`
      span {
        text-decoration: underline;
        color: ${theme.COLORS.PRIMARY};
      }
    `}
  `}
`;
